import React from 'react';
import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Database, X } from 'lucide-react';
import { BrandMark } from '../BrandMark';
import { navItems } from '../../data/navigation';
interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  totalStudents: number;
}
function NavItems({
  collapsed,
  onNavigate,
  idPrefix
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  idPrefix: string;
}) {
  return <nav aria-label="Main navigation" className="flex flex-col gap-1.5 px-3" data-id="element-162" data-mp="35r6v9pxopxsx">
      {navItems.map(item => <NavLink key={item.to} to={item.to} onClick={onNavigate} title={collapsed ? item.label : undefined} className={({
      isActive
    }) => ['group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', collapsed ? 'justify-center' : '', isActive ? 'bg-white/70 text-foreground shadow-sm dark:bg-white/10' : 'text-muted-foreground hover:bg-white/50 hover:text-foreground dark:hover:bg-white/5'].join(' ')} data-id="element-163" data-mp="3aq1geyj4jyg7">
          {({
        isActive
      }) => <>
              {isActive && <motion.span layoutId={`${idPrefix}-active`} className="absolute inset-y-1.5 left-0 w-1 rounded-full brand-gradient" transition={{
          type: 'spring',
          stiffness: 360,
          damping: 30
        }} data-id="element-164" data-mp="29zu5u1wckv7i" />}
              <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'text-brand' : ''}`} strokeWidth={2} data-id="element-165" data-mp="2119tdihdrhrq" />
              {!collapsed && <span className="truncate" data-id="element-166" data-mp="pka6j2dsxv53">{item.label}</span>}
            </>}
        </NavLink>)}
    </nav>;
}
function LiveBadge({
  totalStudents,
  collapsed
}: {
  totalStudents: number;
  collapsed: boolean;
}) {
  if (collapsed) {
    return <div className="mx-3 grid place-items-center rounded-xl border border-[var(--glass-hairline)] py-3" data-id="element-167" data-mp="vzh8xei0md51">
        <Database className="h-4 w-4 text-brand" data-id="element-168" data-mp="i2kdt0uv69ic" />
      </div>;
  }
  return <div className="mx-3 rounded-2xl border border-[var(--glass-hairline)] bg-white/40 p-3 dark:bg-white/5" data-id="element-169" data-mp="m09n02x7ta6b">
      <div className="flex items-center gap-2" data-id="element-170" data-mp="88lavzmyulpc">
        <span className="relative grid h-2 w-2 place-items-center" data-id="element-171" data-mp="d1o76fj4o998">
          <span className="absolute h-2 w-2 rounded-full bg-emerald-500 animate-pulse-ring" data-id="element-172" data-mp="3uhwcw4tjnjz2" />
          <span className="h-2 w-2 rounded-full bg-emerald-500" data-id="element-173" data-mp="3ug1q0kypphsc" />
        </span>
        <p className="text-xs font-semibold text-foreground" data-id="element-174" data-mp="3buzvqq0qgas">Firestore live</p>
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground" data-id="element-175" data-mp="3lhpzsk1rcm0f">
        <span className="font-mono font-semibold text-foreground" data-id="element-176" data-mp="3qgkizn3n0cl2">{totalStudents}</span> record
        {totalStudents === 1 ? '' : 's'} synced from the <span className="font-mono" data-id="element-177" data-mp="36urc9060ubqz">students</span> collection.
      </p>
    </div>;
}
export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
  totalStudents
}: SidebarProps) {
  return <>
      {/* Desktop rail */}
      <aside className={`glass fixed inset-y-0 left-0 z-30 hidden flex-col rounded-r-3xl border-l-0 py-5 transition-[width] duration-300 lg:flex ${collapsed ? 'w-[84px]' : 'w-[264px]'}`} data-id="element-178" data-mp="3bt2k55crac6k">
        <div className={`mb-6 flex items-center px-4 ${collapsed ? 'justify-center' : 'justify-between'}`} data-id="element-179" data-mp="3kth9cfsucbfc">
          <BrandMark size="sm" withText={!collapsed} data-id="element-180" data-mp="3d96qzdq1gtq" />
        </div>

        <NavItems collapsed={collapsed} idPrefix="rail" data-id="element-181" data-mp="3ujtr6099tqhs" />

        <div className="mt-auto flex flex-col gap-3" data-id="element-182" data-mp="3umfzi23zka12">
          <LiveBadge totalStudents={totalStudents} collapsed={collapsed} data-id="element-183" data-mp="cficlby1om0s" />
          <button type="button" onClick={onToggleCollapsed} className="mx-3 flex items-center justify-center gap-2 rounded-xl border border-[var(--glass-hairline)] py-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-184" data-mp="7fw1p95ovhv5">
            {collapsed ? <ChevronRight className="h-4 w-4" data-id="element-185" data-mp="2fp3prukkb4yl" /> : <ChevronLeft className="h-4 w-4" data-id="element-186" data-mp="1ix9qu6adfm4j" />}
            {!collapsed && <span data-id="element-187" data-mp="1iz30zvk70oha">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence data-id="element-188" data-mp="1nxo01rnpl245">
        {mobileOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onCloseMobile} className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden" data-id="element-189" data-mp="1rvva63no356d" />
            <motion.aside initial={{
          x: -300,
          opacity: 0.6
        }} animate={{
          x: 0,
          opacity: 1
        }} exit={{
          x: -300,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 32
        }} className="glass-strong fixed inset-y-0 left-0 z-50 flex w-[272px] flex-col rounded-r-3xl py-5 lg:hidden" aria-label="Sidebar" data-id="element-190" data-mp="38an3qjwjuih9">
              <div className="mb-6 flex items-center justify-between px-4" data-id="element-191" data-mp="34nf20uh0giwp">
                <BrandMark size="sm" data-id="element-192" data-mp="2zpdkidz8grnc" />
                <button type="button" onClick={onCloseMobile} aria-label="Close navigation" className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:text-foreground" data-id="element-193" data-mp="2zqezgtjddyac">
                  <X className="h-4 w-4" data-id="element-194" data-mp="2lr5hboblsu3q" />
                </button>
              </div>
              <NavItems collapsed={false} onNavigate={onCloseMobile} idPrefix="drawer" data-id="element-195" data-mp="nz19oe3vx27z" />
              <div className="mt-auto" data-id="element-196" data-mp="jzakufxx4w63">
                <LiveBadge totalStudents={totalStudents} collapsed={false} data-id="element-197" data-mp="jw4yv9kl6ael" />
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>
    </>;
}